import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import { apiErrorCode } from '../utils/apiError';
import { ProviderInvitationResult, providerCreateErrorKey } from '../components/ProviderInvitationResult';
import type { InvitationDetails } from '../types';
import { useCompanyIdentifierFields } from '../hooks/useCompanyIdentifierFields';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type InspectorVerificationStatus = 'Verifierad' | 'Ej Verifierad';

interface InspectorFormData {
  organizationNumber: string;
  inspectorCompanyName: string;
  email: string;
  phone: string;
  vatNumber: string;
  website: string;
  inspectorPresentation: string;
  verifiedInspector: InspectorVerificationStatus;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  title: string;
  experienceYears: string;
  specialties: string;
  responseTime: string;
  hourlyRate: string;
  fixedPriceAvailable: boolean;
  geographicArea: string;
  languages: string;
  initialMeetingFree: boolean;
  availability: boolean;
  leadTime: string;
  notes: string;
}

const emptyFormData: InspectorFormData = {
  organizationNumber: '',
  inspectorCompanyName: '',
  email: '',
  phone: '',
  vatNumber: '',
  website: '',
  inspectorPresentation: '',
  verifiedInspector: 'Ej Verifierad',
  address: '',
  postalCode: '',
  city: '',
  country: 'Sverige',
  title: '',
  experienceYears: '',
  specialties: '',
  responseTime: '',
  hourlyRate: '',
  fixedPriceAvailable: false,
  geographicArea: '',
  languages: 'Svenska',
  initialMeetingFree: false,
  availability: true,
  leadTime: '',
  notes: ''
};

export function AdminInspectorCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [nextInspectorNumber, setNextInspectorNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [formData, setFormData] = useState<InspectorFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Organisation and VAT number checked like "Skapa hantverkare" (869fjt7ma).
  const identifiers = useCompanyIdentifierFields(formData.organizationNumber, formData.vatNumber, patch => {
    setFormData(prev => ({ ...prev, ...patch }));
    setFormErrors(prev => ({ ...prev, organizationNumber: '', vatNumber: '' }));
  });
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const nextNumberData = await adminApi.getNextInspectorNumber();
      setNextInspectorNumber(nextNumberData.nextInspectorNumber);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

  };

  const handleNotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const handleInspectorPresentationChange = (value: string) => {
    setFormData(prev => ({ ...prev, inspectorPresentation: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.email');
    }

    if (identifiers.organizationNumberError) {
      errors.organizationNumber = identifiers.organizationNumberError;
    }
    if (identifiers.vatNumberError) {
      errors.vatNumber = identifiers.vatNumberError;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    setMessage(null);

    try {
      const result = await adminApi.createInspector({
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean) as any,
        languages: formData.languages.split(',').map(s => s.trim()).filter(Boolean) as any,
        experienceYears: Number(formData.experienceYears) || 0 as any,
      } as any);
      // Without an account the person is invited (ClickUp 869fjt7ma).
      if (result.outcome === 'invited') {
        setInvitation(result.invitation);
        setIsSaving(false);
        return;
      }
      setMessage({ type: 'success', text: t('inspectors.form.saveSuccess') });
      setTimeout(() => {
        navigate('/admin/inspectors');
      }, 1000);
    } catch (error) {
      console.error('Failed to create inspector:', error);
      setMessage({ type: 'error', text: t(providerCreateErrorKey('inspector', apiErrorCode(error))) });
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('inspectorCreate.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminInspectors.title'), path: '/admin/inspectors' },
            { label: t('inspectorCreate.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('inspectorCreate.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <div className="d-flex gap-2">
            <Link to="/admin/inspectors" className="btn btn-light-brand">
              <i className="feather-arrow-left me-2"></i>
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {invitation ? <ProviderInvitationResult type="inspector" invitation={invitation} /> : <>
      {/* Tabs Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="nav-tabs-wrapper">
          <ul className="nav nav-tabs nav-tabs-custom-style" role="tablist">
            <li className="nav-item" role="presentation">
              <button className="nav-link active">
                {t('inspectorCreate.tabs.details')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        <div className="tab-pane fade show active">
          <Card>
            <div className="card-header">
              <h5 className="card-title mb-0">{t('inspectorCreate.tabs.details')}</h5>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <p className="text-muted">{t('providerInvite.help.inspector')}</p>
                <div className="row">
                  {/* Inspector Number (read-only, auto-generated) */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('inspectors.columns.inspectorNumber')}</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={nextInspectorNumber}
                      disabled
                    />
                    <small className="text-muted">{t('inspectorCreate.inspectorNumberHelp')}</small>
                  </div>

                  {/* Verified Status */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('inspectors.columns.verifiedInspector')}</label>
                    <select
                      name="verifiedInspector"
                      className="form-select"
                      value={formData.verifiedInspector}
                      onChange={handleInputChange}
                    >
                      <option value="Ej Verifierad">{t('inspectors.status.notVerified')}</option>
                      <option value="Verifierad">{t('inspectors.status.verified')}</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  {/* Organization Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="organizationNumber"
                      label={t('inspectors.columns.organizationNumber')}
                      value={formData.organizationNumber}
                      placeholder={t('auth.bankid.organizationNumberExample')}
                      onChange={event => identifiers.changeOrganizationNumber(event.target.value)}
                      onBlur={() => void identifiers.checkOrganizationNumber()}
                      error={formErrors.organizationNumber || identifiers.organizationNumberError}
                      maxLength={32}
                    >
                      <TestOrgNumberButton onGenerated={value => { setFormData(previous => withTestOrgNumber(previous, value)); setFormErrors(previous => ({ ...previous, organizationNumber: '' })); }} />
                    </FormInput>

                  </div>
                </div>

                <div className="row">
                  {/* Inspector Company Name */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="inspectorCompanyName"
                      label={t('inspectors.columns.inspectorCompanyName')}
                      value={formData.inspectorCompanyName}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Phone */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      type="tel"
                      name="phone"
                      label={t('inspectors.columns.phone')}
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={48}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* VAT Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="vatNumber"
                      label={t('inspectors.columns.vatNumber')}
                      value={formData.vatNumber}
                      placeholder={t('auth.bankid.vatExample')}
                      onChange={event => identifiers.changeVatNumber(event.target.value)}
                      error={formErrors.vatNumber || identifiers.vatNumberError}
                      maxLength={32}
                    >
                      {identifiers.vatNumberWarning && <div className="text-warning small mt-1" data-testid="provider-vat-warning">
                        <i className="feather-alert-triangle me-1" aria-hidden="true"></i>{identifiers.vatNumberWarning}
                      </div>}
                    </FormInput>
                  </div>

                  {/* Contact Email */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="email"
                      type="email"
                      label={`${t('inspectors.columns.email')} *`}
                      value={formData.email}
                      onChange={handleInputChange}
                      error={formErrors.email}
                      maxLength={128}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* Website */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="website"
                      label={t('inspectors.columns.website')}
                      value={formData.website}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                </div>



                <hr className="my-3" />
                <h6 className="fw-bold mb-3">{t('marketplace.provider.practicalInfo', 'Marknadsplatsinformation')}</h6>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput name="title" label={t('inspectors.columns.title', 'Titel')} value={formData.title} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderTitle')} maxLength={64} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput name="experienceYears" type="number" label={t('inspectors.columns.experienceYears', 'Erfarenhetsår')} value={formData.experienceYears} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput name="specialties" label={t('inspectors.columns.specialties', 'Specialistområden')} value={formData.specialties} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderSpecialties')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput name="languages" label={t('inspectors.columns.languages', 'Språk')} value={formData.languages} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderLanguages')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput name="hourlyRate" label={t('inspectors.columns.hourlyRate', 'Timarvode')} value={formData.hourlyRate} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderHourlyRate')} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput name="responseTime" label={t('inspectors.columns.responseTime', 'Svarstid')} value={formData.responseTime} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderResponseTime')} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput name="geographicArea" label={t('inspectors.columns.geographicArea', 'Geografiskt område')} value={formData.geographicArea} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderGeographicArea')} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput name="leadTime" label={t('inspectors.columns.leadTime', 'Ledtid')} value={formData.leadTime} onChange={handleInputChange} placeholder={t('adminInspectors.placeholderLeadTime')} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="form-check mt-4">
                      <input type="checkbox" className="form-check-input" id="fixedPriceAvailable" name="fixedPriceAvailable" checked={formData.fixedPriceAvailable} onChange={e => setFormData(p => ({ ...p, fixedPriceAvailable: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="fixedPriceAvailable">{t('inspectors.columns.fixedPriceAvailable', 'Fast pris möjligt')}</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-check mt-4">
                      <input type="checkbox" className="form-check-input" id="initialMeetingFree" name="initialMeetingFree" checked={formData.initialMeetingFree} onChange={e => setFormData(p => ({ ...p, initialMeetingFree: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="initialMeetingFree">{t('inspectors.columns.initialMeetingFree', 'Gratis inledande möte')}</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="form-check mt-4">
                      <input type="checkbox" className="form-check-input" id="availability" name="availability" checked={formData.availability} onChange={e => setFormData(p => ({ ...p, availability: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="availability">{t('inspectors.columns.availability', 'Tillgänglig för nya uppdrag')}</label>
                    </div>
                  </div>
                </div>

                <hr className="my-3" />
                <h6 className="fw-bold mb-3">{t('inspectors.columns.address', 'Adress')}</h6>

                <div className="row">
                  {/* Address */}
                  <div className="col-md-6 mb-3">
                    <AddressAutocomplete
                      name="address"
                      onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                      label={t('inspectors.columns.address')}
                      value={formData.address}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="postalCode"
                      label={t('inspectors.columns.postalCode')}
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      maxLength={16}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* City */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="city"
                      label={t('inspectors.columns.city')}
                      value={formData.city}
                      onChange={handleInputChange}
                      maxLength={64}
                    />
                  </div>

                  {/* Country */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="country"
                      label={t('inspectors.columns.country', 'Land')}
                      value={formData.country}
                      onChange={handleInputChange}
                      maxLength={64}
                    />
                  </div>
                </div>

                {/* Inspector Presentation (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('inspectors.columns.inspectorPresentation')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.inspectorPresentation}
                    onChange={handleInspectorPresentationChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>

                {/* Notes (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('inspectors.columns.notes')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.notes}
                    onChange={handleNotesChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <Button type="submit" isLoading={isSaving} disabled={isSaving}>
                  <i className="feather-save me-2"></i>
                  {t('inspectorCreate.createButton')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
      </>}
    </DashboardLayout>
  );
}
